import { AvailabilityCalendar } from "@/components/admin/AvailabilityCalendar";
import { NoPropertySelected } from "@/components/admin/NoPropertySelected";
import { RoomTypeFormDialog } from "@/components/admin/RoomTypeFormDialog";
import { BrandLoader } from "@/components/brand/BrandLoader";
import { EmptyState } from "@/components/brand/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { roomTypeService } from "@/lib/api";
import { formatKrw } from "@/lib/format";
import { useSelectedProperty } from "@/stores/property";
import type { RoomType, RoomTypeCreate, RoomTypeUpdate } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { LayoutGrid, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export function RoomTypesPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RoomType | null>(null);

  const propertyId = useSelectedProperty();

  const { data: roomTypes, isLoading } = useQuery({
    queryKey: ["room-types", propertyId],
    queryFn: () => roomTypeService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["room-types"] });

  const createMutation = useMutation({
    mutationFn: (body: RoomTypeCreate) => roomTypeService.create(body),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      toast.success("객실 타입이 등록되었습니다.");
    },
    onError: () => toast.error("등록에 실패했습니다."),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: RoomTypeUpdate }) =>
      roomTypeService.update(id, body),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setEditing(null);
      toast.success("저장되었습니다.");
    },
    onError: () => toast.error("저장에 실패했습니다."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => roomTypeService.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success("삭제되었습니다.");
    },
    onError: (error) => {
      const detail = isAxiosError(error) ? error.response?.data?.detail : null;
      toast.error(typeof detail === "string" ? detail : "삭제에 실패했습니다.");
    },
  });

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (rt: RoomType) => {
    setEditing(rt);
    setDialogOpen(true);
  };

  const handleSubmit = (body: RoomTypeCreate | RoomTypeUpdate) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, body: body as RoomTypeUpdate });
    } else {
      if (!propertyId) return;
      createMutation.mutate({ ...(body as RoomTypeCreate), property_id: propertyId });
    }
  };

  if (!propertyId) {
    return <NoPropertySelected />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">객실 타입</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            객실 타입별 기본요금과 정원을 관리합니다. 예약 채널 연동에 사용됩니다.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" /> 새 타입
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <BrandLoader className="py-12" size={44} label="불러오는 중…" />
        ) : roomTypes && roomTypes.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>타입명</TableHead>
                <TableHead>기본요금</TableHead>
                <TableHead>정원</TableHead>
                <TableHead>상태</TableHead>
                <TableHead className="text-right">관리</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roomTypes.map((rt) => (
                <TableRow key={rt.id}>
                  <TableCell className="font-medium">{rt.name}</TableCell>
                  <TableCell className="tabular-nums">{formatKrw(rt.base_rate)}</TableCell>
                  <TableCell className="tabular-nums">
                    {rt.capacity != null ? `${rt.capacity}명` : "-"}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        rt.is_active
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {rt.is_active ? "활성" : "비활성"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="수정"
                        onClick={() => openEdit(rt)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="삭제"
                        onClick={() => {
                          if (!window.confirm("이 객실 타입을 삭제할까요?")) return;
                          deleteMutation.mutate(rt.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            title="아직 등록된 객실 타입이 없어요"
            description="객실 타입을 등록하면 예약 관리와 채널 연동에 활용할 수 있어요."
            action={
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4" /> 새 타입
              </Button>
            }
          />
        )}
      </Card>

      {roomTypes && roomTypes.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <LayoutGrid className="h-4 w-4" />
              가용 현황 캘린더
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AvailabilityCalendar roomTypes={roomTypes} />
          </CardContent>
        </Card>
      ) : null}

      <RoomTypeFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        roomType={editing}
        isPending={createMutation.isPending || updateMutation.isPending}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
